/**
 * Making a community: the rules, separate from the screen that asks for them.
 *
 * A community is not a set of channels that happen to be live. It is a
 * standing list with a name and a link, which is why this can be built from
 * anybody on Twitch rather than only from whoever is streaming right now — a
 * room whose membership could only be chosen from the currently-live is a room
 * you would have to rebuild every evening.
 */
import { ROOM_ROSTER_MAX, RoomSlug } from '@tomo/contracts';

/** One prospective member, as the picker knows them. */
export interface Candidate {
  readonly login: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly live: boolean;
}

/**
 * A handle suggested from the name, which the person may overwrite.
 *
 * Suggested rather than derived: the name is what the room is CALLED and the
 * handle is what it is REACHED at, and the two stop agreeing the first time
 * somebody renames a room people already have links to. Offering one saves
 * the common case from typing it twice without welding them together.
 */
export function slugFrom(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    // Anything that is not a letter, digit or hyphen becomes a hyphen, then
    // runs collapse — so "567 Crew!!" is "567-crew" and not "567-crew---".
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    // A trailing hyphen can reappear after the cut.
    .replace(/-+$/g, '');
  return slug;
}

/** Whether this is a handle the API will accept, without asking it. */
export function slugIsValid(slug: string): boolean {
  return RoomSlug.safeParse(slug).success;
}

/**
 * Add somebody, or say why not.
 *
 * Returns the list unchanged for a duplicate rather than refusing loudly: the
 * same person reachable from both the follow list and a search is one person,
 * and pressing the second one is not a mistake worth a message.
 */
export function withMember(
  members: readonly Candidate[],
  candidate: Candidate,
): readonly Candidate[] {
  if (members.some((m) => m.login === candidate.login)) return members;
  if (members.length >= ROOM_ROSTER_MAX) return members;
  return [...members, candidate];
}

export function withoutMember(
  members: readonly Candidate[],
  login: string,
): readonly Candidate[] {
  return members.filter((m) => m.login !== login);
}

/**
 * Twitch's search results, with the person you typed moved to the top.
 *
 * Helix ranks by its own idea of relevance, which puts live and popular
 * channels first — so typing "libtron" exactly listed libtron tenth, behind
 * nine people whose names merely resemble it. Somebody who types a whole
 * handle is naming one person. Exact matches lead, then names that begin with
 * what was typed, then everything else in the order Twitch gave; a stable
 * sort, so within each band Twitch's ordering still stands.
 */
export function rankByName<T extends { readonly login: string; readonly displayName: string }>(
  hits: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...hits];
  const band = (hit: T): number => {
    const login = hit.login.toLowerCase();
    const shown = hit.displayName.toLowerCase();
    if (login === needle || shown === needle) return 0;
    if (login.startsWith(needle) || shown.startsWith(needle)) return 1;
    return 2;
  };
  return hits
    .map((hit, index) => ({ hit, index, band: band(hit) }))
    .sort((a, b) => a.band - b.band || a.index - b.index)
    .map((row) => row.hit);
}

export interface Draft {
  readonly name: string;
  readonly slug: string;
  readonly members: readonly Candidate[];
}

/**
 * What is stopping this from being created, or null when nothing is.
 *
 * One message at a time, in the order somebody fills the form in, because a
 * list of everything wrong with a half-typed form is noise. Returning the
 * reason rather than a boolean is what lets the button explain itself instead
 * of just being grey.
 */
export function blockedBecause(draft: Draft): string | null {
  if (draft.name.trim().length === 0) return 'Give the community a name.';
  if (draft.name.trim().length > 80) return 'That name is longer than 80 characters.';
  if (!slugIsValid(draft.slug)) {
    return 'A link is 3–40 lowercase letters, digits or hyphens.';
  }
  if (draft.members.length === 0) return 'Add at least one streamer.';
  return null;
}

/** The body `POST /v1/rooms` takes. Wire shape — snake_case. */
export function createBody(draft: Draft, visibility: 'public' | 'private') {
  return {
    kind: 'community' as const,
    visibility,
    name: draft.name.trim(),
    slug: draft.slug,
    twitch_logins: draft.members.map((m) => m.login),
  };
}

export { ROOM_ROSTER_MAX };
